<template>
  <div
    v-if="authStore.details.userType == 1"
    class="access-category"
  >
    <div class="font-medium mb-4">Access Mode</div>
    <RadioTabbed
      v-model="state.all"
      :options="[
        { value: true, label: 'All Access (Retain Staff)' },
        { value: false, label: 'Specific Access' }
      ]"
      @option-change="emitGrants"
    />
  </div>

  <template v-if="!state.all">
    <div class="access-category">
      <div class="font-medium mb-1">Orgs</div>
      <div class="text-sm text-secondary mb-4">
        Grants access to every dealership in the org, including sites added in the future.
      </div>
      <Select
        v-if="orgOptions.length"
        v-model="state.orgs"
        mode="multiple"
        chips
        :chips-displayed="10"
        auto-expand
        :close-on-select="false"
        searchable
        :hide-selected="true"
        value-key="id"
        label-key="name"
        :placeholder="'None selected'"
        :options="orgOptions"
        @change="emitGrants"
      />
      <div
        v-else
        class="text-warning flex gap-2 items-center mt-2"
      >
        <Icon type="triangle-exclamation" />
        <div>No orgs available</div>
      </div>
    </div>

    <div class="access-category">
      <div class="font-medium mb-1">Manufacturers</div>
      <div class="text-sm text-secondary mb-4">
        Grants access to every franchised dealership of the brand, across all orgs. Multi-brand and used-car sites are
        never included.
      </div>
      <Select
        v-if="manufacturerOptions.length"
        v-model="state.manufacturers"
        mode="multiple"
        chips
        :chips-displayed="10"
        auto-expand
        :close-on-select="false"
        searchable
        :hide-selected="true"
        value-key="id"
        label-key="name"
        :placeholder="'None selected'"
        :options="manufacturerOptions"
        @change="emitGrants"
      />
      <div
        v-else
        class="text-warning flex gap-2 items-center mt-2"
      >
        <Icon type="triangle-exclamation" />
        <div>No manufacturers available</div>
      </div>
      <div
        v-if="state.manufacturers.length"
        class="mt-4"
      >
        <div class="text-sm text-secondary mb-2">Limit manufacturer access to regions (optional)</div>
        <Select
          v-model="state.manufacturerRegions"
          mode="multiple"
          chips
          :close-on-select="false"
          value-key="id"
          label-key="name"
          :placeholder="'All regions'"
          :options="regionOptions"
          @change="emitGrants"
        />
      </div>
    </div>

    <div class="access-category">
      <div class="font-medium mb-1">Dealerships</div>
      <div class="text-sm text-secondary mb-4">Grants access to individual sites.</div>
      <Select
        v-if="dealershipOptions.length"
        v-model="state.dealerships"
        mode="multiple"
        chips
        :chips-displayed="10"
        auto-expand
        searchable
        :close-on-select="false"
        :hide-selected="true"
        value-key="id"
        label-key="name"
        :placeholder="'None selected'"
        :options="dealershipOptions"
        @change="emitGrants"
      />
      <div
        v-else
        class="text-warning flex gap-2 items-center mt-2"
      >
        <Icon type="triangle-exclamation" />
        <div>No dealerships available</div>
      </div>
    </div>
  </template>

  <Alert
    v-if="resolvedDealershipCount === 0"
    severity="warning"
    icon="triangle-exclamation"
    title="No Access"
    message="The current selection doesn't give access to any dealerships."
    :show-dismiss-button="false"
    :show-close-button="false"
    class="mt-4"
  />
  <Alert
    v-else
    :severity="resolvedDealershipCount > 10 ? 'warning' : 'info'"
    :icon="resolvedDealershipCount > 10 ? 'triangle-exclamation' : 'circle-info'"
    :title="`Access to ${resolvedDealershipCount} dealership${resolvedDealershipCount === 1 ? '' : 's'}`"
    :message="
      resolvedDealershipCount > 10
        ? `You are giving access to ${resolvedDealershipCount} dealerships. Please make sure this is correct.`
        : 'Based on the current selection.'
    "
    :show-dismiss-button="false"
    :show-close-button="false"
    class="mt-4"
  />
</template>

<script setup>
import { watch, ref, computed } from 'vue'
// components
import Select from '@/components/forms/Select.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'
import Icon from '@/components/icon/Icon.vue'
import Alert from '@/components/alert/Alert.vue'
// stores
import { useAuthStore } from '@/stores/AuthStore'
import { useAccessStore } from '@/stores/AccessStore'
// utilities
import { resolveDealershipIdsFromGrants } from '@/utilities/access/accessGroupings'

const props = defineProps({
  /** Array of access grants: { scopeType, scopeId, region } */
  modelValue: {
    type: Array,
    default: () => []
  }
})
const emit = defineEmits(['update:modelValue'])
const authStore = useAuthStore()
const accessStore = useAccessStore()

// Editor state distilled from the grants array
const state = ref({
  all: false,
  orgs: [],
  manufacturers: [],
  manufacturerRegions: [],
  dealerships: []
})

// Options are limited to the session user's own resolved access
const orgOptions = computed(() => accessStore.accessAllowed.groups)
const manufacturerOptions = computed(() => accessStore.accessAllowed.manufacturers)
const dealershipOptions = computed(() => accessStore.accessAllowed.dealerships)
const regionOptions = computed(() => accessStore.accessAllowed.regions)

/**
 * Convert the editor state into a grants array.
 * Manufacturer region qualifiers produce one grant per manufacturer/region pair.
 * @returns {Array<{scopeType: string, scopeId: number|null, region: string|null}>}
 */
function stateToGrants() {
  // All access is a single grant that supersedes everything else
  if (state.value.all) {
    return [{ scopeType: 'all', scopeId: null, region: null }]
  }

  const grants = []
  state.value.orgs.forEach(orgId => {
    grants.push({ scopeType: 'org', scopeId: orgId, region: null })
  })
  state.value.manufacturers.forEach(manufacturerId => {
    // No region qualifier means the grant covers all regions
    if (state.value.manufacturerRegions.length === 0) {
      grants.push({ scopeType: 'manufacturer', scopeId: manufacturerId, region: null })
    } else {
      state.value.manufacturerRegions.forEach(region => {
        grants.push({ scopeType: 'manufacturer', scopeId: manufacturerId, region })
      })
    }
  })
  state.value.dealerships.forEach(dealershipId => {
    grants.push({ scopeType: 'dealership', scopeId: dealershipId, region: null })
  })
  return grants
}

/**
 * Distil a grants array into editor state.
 * Manufacturer region qualifiers are collapsed into one shared region set;
 * differing per-manufacturer regions are unioned (an accepted simplification).
 * @param {Array<{scopeType: string, scopeId: number|null, region?: string|null}>} grants
 */
function grantsToState(grants) {
  const newState = {
    all: false,
    orgs: [],
    manufacturers: [],
    manufacturerRegions: [],
    dealerships: []
  }

  for (const grant of grants ?? []) {
    // Each scope type maps onto its own selector
    if (grant.scopeType === 'all') {
      newState.all = true
    } else if (grant.scopeType === 'org') {
      if (!newState.orgs.includes(grant.scopeId)) newState.orgs.push(grant.scopeId)
    } else if (grant.scopeType === 'manufacturer') {
      if (!newState.manufacturers.includes(grant.scopeId)) newState.manufacturers.push(grant.scopeId)
      if (grant.region && !newState.manufacturerRegions.includes(grant.region)) {
        newState.manufacturerRegions.push(grant.region)
      }
    } else if (grant.scopeType === 'dealership') {
      if (!newState.dealerships.includes(grant.scopeId)) newState.dealerships.push(grant.scopeId)
    }
  }

  state.value = newState
}

/** Emit the current state as a grants array */
function emitGrants() {
  emit('update:modelValue', stateToGrants())
}

// Client-side preview of what the grants resolve to
const resolvedDealershipCount = computed(() => {
  return resolveDealershipIdsFromGrants(stateToGrants(), accessStore.accessAllowed.dealerships).length
})

// Sync incoming grants into the editor state (skip echoes of our own emit to avoid loops)
watch(
  () => props.modelValue,
  newValue => {
    if (JSON.stringify(newValue ?? []) === JSON.stringify(stateToGrants())) {
      return
    }
    grantsToState(newValue)
  },
  { immediate: true }
)

// Emit whenever the state changes (covers select clears and region edits)
watch(
  state,
  () => {
    emitGrants()
  },
  { deep: true }
)
</script>

<style>
.access-category {
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-lg);
  margin-bottom: var(--s-4);
  padding: var(--s-4);
}
</style>
